import React, { useContext } from "react";
import { reviewFieldId, sourceLabel } from "./Fields";
import SourceButton from "./sources/SourceButton";
import HtsInput from "./HtsInput";
import ReviewField from "./ReviewField";
import ReviewIssueHint, {
  CloseFieldEdit,
  ReviewIssueContext,
} from "./ReviewIssueHint";

function GoodsSources({ item, documents, onShowSource }) {
  return (
    <span className="premium-source-location">
      <span className="premium-source">
        {[
          ...new Set(
            (item.sources || [])
              .map((source) => sourceLabel(source, documents))
              .filter(Boolean),
          ),
        ].join(", ") || "Wpis ręczny"}
      </span>
      {item.sources?.length > 0 && (
        <SourceButton
          highlighted={item.sources.some((entry) => entry.regions?.length)}
          onClick={
            onShowSource &&
            (() => onShowSource(item.sources, { item: item.id }))
          }
        />
      )}
    </span>
  );
}

/** Pozycje zachowują identyfikator również po korekcie kodu produktu. */
export default function GoodsEditor({
  items,
  grossWeight,
  options,
  documents,
  readOnly,
  setItems,
  onWeightChange,
  onShowSource,
}) {
  const selection = useContext(ReviewIssueContext);
  const itemsId = reviewFieldId("items");
  const indicateEmpty = selection?.id === itemsId;
  const addingFromIssue = indicateEmpty && selection.editing;
  const columns = [
    ["sap", options.productCodeLabel || "Kod produktu"],
    ["hts", "HTS"],
    ["description", "Opis towaru"],
    ["origin", "Kraj pochodzenia"],
    ["quantity", options.quantityLabel || "Ilość"],
    ["unit", "Jednostka"],
  ];
  const updateItem = (index, field, value) =>
    setItems(
      items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  return (
    <>
      {!readOnly && <p className="small text-muted">{options.goodsHint}</p>}
      {readOnly && !items.length ? (
        <div
          id={addingFromIssue ? undefined : itemsId}
          tabIndex={-1}
          className={indicateEmpty ? "premium-issue-target" : "text-muted"}
          aria-describedby={
            indicateEmpty && !addingFromIssue ? `${itemsId}-hint` : undefined
          }
        >
          Brak pozycji towarowych.
          {indicateEmpty && !addingFromIssue && (
            <ReviewIssueHint selection={selection} />
          )}
        </div>
      ) : (
        <div
          className="table-responsive"
          id={readOnly ? itemsId : undefined}
          tabIndex={readOnly ? -1 : undefined}
        >
          <table
            className={
              readOnly ? "table premium-read-items" : "table premium-items"
            }
            aria-label="Pozycje towarowe"
          >
            <thead>
              <tr>
                <th scope="col">Lp.</th>
                {columns.map(([field, label]) => (
                  <th scope="col" key={field} data-field={field}>
                    {label}
                  </th>
                ))}
                {!readOnly && (
                  <th scope="col">
                    <span className="visually-hidden">Akcje</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id} id={`premium-item-${item.id}`}>
                  <th scope="row">
                    <span
                      className={readOnly ? undefined : "premium-item-number"}
                    >
                      {index + 1}
                    </span>
                  </th>
                  {columns.map(([field, label]) => {
                    const id = reviewFieldId("items." + field, item.id);
                    const indicated = readOnly && selection?.id === id;
                    const inlineEdit = indicated && selection.editing;
                    const showValue =
                      readOnly && !inlineEdit && !addingFromIssue;
                    return (
                      <td
                        key={field}
                        data-field={field}
                        id={showValue ? id : undefined}
                        className={
                          indicated ? "premium-issue-target" : undefined
                        }
                        aria-describedby={
                          indicated && !inlineEdit ? `${id}-hint` : undefined
                        }
                        tabIndex={showValue ? -1 : undefined}
                      >
                        {indicated && (
                          <div className="premium-inline-caption">
                            <strong className="premium-indicated-column">
                              {label} · poz. {index + 1}
                            </strong>
                            {inlineEdit && (
                              <CloseFieldEdit onClick={selection.dismiss} />
                            )}
                          </div>
                        )}
                        {showValue ? (
                          <span className="premium-item-value">
                            {item[field] === "" ? "-" : (item[field] ?? "-")}
                          </span>
                        ) : field === "hts" ? (
                          <HtsInput
                            id={id}
                            label={`${label} ${index + 1}`}
                            value={item[field] ?? ""}
                            onChange={(event) =>
                              updateItem(index, field, event.target.value)
                            }
                          />
                        ) : field === "description" ? (
                          <div className="premium-goods-description">
                            <span className="form-control" aria-hidden="true">
                              {item[field]}
                              {"\u200b"}
                            </span>
                            <textarea
                              id={id}
                              aria-label={`${label} ${index + 1}`}
                              className="form-control"
                              rows={1}
                              value={item[field] ?? ""}
                              onChange={(event) =>
                                updateItem(index, field, event.target.value)
                              }
                            />
                          </div>
                        ) : (
                          <input
                            id={id}
                            autoFocus={addingFromIssue && field === "sap"}
                            aria-label={`${label} ${index + 1}`}
                            className="form-control"
                            value={item[field] ?? ""}
                            maxLength={field === "origin" ? 2 : 100}
                            onChange={(event) =>
                              updateItem(index, field, event.target.value)
                            }
                          />
                        )}
                        {field === "sap" && (
                          <GoodsSources
                            item={item}
                            documents={documents}
                            onShowSource={onShowSource}
                          />
                        )}
                        {indicated && !inlineEdit && (
                          <ReviewIssueHint selection={selection} />
                        )}
                      </td>
                    );
                  })}
                  {!readOnly && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        aria-label={`Usuń pozycję ${index + 1}`}
                        onClick={() =>
                          setItems(items.filter((_, i) => i !== index))
                        }
                      >
                        Usuń
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(!readOnly || (addingFromIssue && !items.length)) && (
        <button
          id={itemsId}
          type="button"
          className="btn btn-outline-primary premium-space-after"
          onClick={() =>
            setItems([
              ...items,
              {
                id: crypto.randomUUID(),
                sap: "",
                hts: "",
                description: "",
                origin: "",
                quantity: "",
                unit: "",
                sources: [],
              },
            ])
          }
        >
          Dodaj pozycję
        </button>
      )}
      {addingFromIssue && <CloseFieldEdit onClick={selection.dismiss} />}
      <ReviewField
        readOnly={readOnly}
        id={reviewFieldId("grossWeight")}
        label={options.grossWeightLabel || "Łączna masa brutto (kg)"}
        inputMode="decimal"
        value={grossWeight}
        onChange={(event) => onWeightChange(event.target.value)}
      />
    </>
  );
}
